import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import PollIcon from "@/components/PollIcon";
import { fetchVisits, trackVisit } from "@/lib/api";
import { getConsent, CONSENT_ACCEPTED } from "@/components/CookieBanner";

const VISITOR_KEY = "loftebarometer_visitor_id";

function ensureVisitorId() {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
        id =
            (crypto.randomUUID && crypto.randomUUID()) ||
            `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
}

export default function Footer() {
    const [visits, setVisits] = useState(null);

    useEffect(() => {
        function syncVisits() {
            // Kun track hvis brugeren har givet fuld accept.
            // "Kun nødvendige" → kun vis det samlede tal uden at spore én selv.
            if (getConsent() === CONSENT_ACCEPTED) {
                const id = ensureVisitorId();
                trackVisit(id).catch(() => {});
            }
            fetchVisits().then(setVisits).catch(() => {});
        }
        syncVisits();
        window.addEventListener("consent-changed", syncVisits);
        return () => window.removeEventListener("consent-changed", syncVisits);
    }, []);

    return (
        <footer
            className="bg-royal-deep text-white/80 mt-24"
            data-testid="main-footer"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-2.5 mb-4">
                            <div className="w-9 h-9 bg-white flex items-center justify-center rounded-sm">
                                <PollIcon size={22} color="#BB1B2D" />
                            </div>
                            <div className="font-serif-dm text-2xl text-white">
                                Løftebarometeret
                            </div>
                        </div>
                        <p className="text-sm leading-relaxed max-w-md">
                            Et uafhængigt overblik over danske politikeres valgløfter —
                            hvem holder hvad, hvem bryder deres ord, og hvor ofte møder
                            vi det rene valgflæsk?
                        </p>
                    </div>

                    <div>
                        <h4 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">
                            Udforsk
                        </h4>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/partier" className="hover:text-white" data-testid="footer-parties">Partier</Link></li>
                            <li><Link to="/lofter" className="hover:text-white" data-testid="footer-promises">Løfter</Link></li>
                            <li><Link to="/afstemninger" className="hover:text-white" data-testid="footer-polls">Afstemninger</Link></li>
                            <li><Link to="/statistik" className="hover:text-white" data-testid="footer-stats">Statistik</Link></li>
                            <li><Link to="/artikler" className="hover:text-white" data-testid="footer-articles">Artikler</Link></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">
                            Om
                        </h4>
                        <ul className="space-y-2 text-sm">
                            <li>Uafhængigt initiativ</li>
                            <li>Data fra åbne kilder</li>
                            <li>
                                <Link
                                    to="/cookies"
                                    className="hover:text-white"
                                    data-testid="footer-cookies"
                                >
                                    Cookie- og privatlivspolitik
                                </Link>
                            </li>
                            <li>
                                <a
                                    href="https://simonkarlsson.dk/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-white underline-offset-2 hover:underline"
                                    data-testid="footer-simon-karlsson"
                                >
                                    Udtænkt af Simon Karlsson
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs text-white/50">
                    <p>Bygget til dansk demokrati.</p>
                    {visits && (
                        <div
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70"
                            data-testid="visits-counter"
                            title="Unikke besøgende (pr. browser)"
                        >
                            <Eye className="w-3.5 h-3.5" />
                            <span>
                                <strong className="text-white">{visits.total.toLocaleString("da-DK")}</strong> unikke besøg
                                {visits.today > 0 && (
                                    <span className="text-white/50">
                                        {" "}· <strong className="text-white/80">{visits.today.toLocaleString("da-DK")}</strong> i dag
                                    </span>
                                )}
                            </span>
                        </div>
                    )}
                    <p>Stoler du på politikere? — Tjek deres historik før Dit næste valg.</p>
                </div>
            </div>
        </footer>
    );
}
